// Phase 16.3 — Generic "feature shell" for in-flight Lifted admin pages.
//
// Renders a polished page header (logo + breadcrumb + title), an optional
// "Connection Required" banner listing the missing credentials, and slots
// where real data can be plugged in once a feature is wired up. Replaces
// the old crashy/blank pages for the menu items that are still scoped for
// later phases.

import React from "react";
import { Link } from "react-router-dom";
import { Lock, ArrowRight, ArrowLeft } from "lucide-react";
import BrandMark from "@/components/lifted/BrandMark";

export default function AdminFeatureShell({
  brandTag = "logo.latm.primary",
  breadcrumb = "LiftedAtTheMoment / Admin",
  title,
  description,
  missingCredentials = [],   // [{ env: "MAPBOX_TOKEN", purpose: "Live driver map" }]
  status,                    // optional small chip text (e.g. "Shell ready")
  testId,
  children,
}) {
  return (
    <div className="space-y-6" data-testid={testId || "admin-feature-shell"}>
      <header className="space-y-3">
        <div className="flex items-center gap-3">
          <BrandMark tag={brandTag} size="sm" />
          <div className="font-bebas text-[0.65rem] tracking-[0.22em] text-lifted-dim">
            {breadcrumb}
          </div>
        </div>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h1 className="font-anton text-3xl sm:text-4xl uppercase text-white leading-[0.95]">
            {title}
          </h1>
          {status && (
            <span className="font-bebas text-[0.6rem] tracking-[0.22em] text-lifted-green border border-lifted-green/40 rounded-full px-2.5 py-1">
              {status}
            </span>
          )}
        </div>
        {description && (
          <p className="text-lifted-dim text-sm max-w-2xl">{description}</p>
        )}
      </header>

      {missingCredentials.length > 0 && (
        <div
          className="glass-panel p-5 border border-amber-400/30 bg-amber-500/5"
          data-testid="admin-shell-connection-required"
        >
          <div className="flex items-center gap-2 font-bebas tracking-[0.22em] text-amber-300 text-xs">
            <Lock className="h-3.5 w-3.5" /> CONNECTION REQUIRED
          </div>
          <p className="text-sm text-lifted-text mt-2">
            This feature is shipped as a shell. Provide the credentials below and the live
            integration will activate automatically — no code change required.
          </p>
          <ul className="mt-3 space-y-1 text-sm">
            {missingCredentials.map((c) => (
              <li key={c.env} className="flex flex-wrap items-center gap-2">
                <code className="text-lifted-green font-mono text-[0.7rem] bg-black/40 px-2 py-0.5 rounded">{c.env}</code>
                <span className="text-lifted-dim">{c.purpose}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {children}

      <div className="pt-4 border-t border-white/5 flex flex-wrap gap-3">
        <Link to="/dashboard" className="btn-ghost !py-2 !px-4 text-xs inline-flex items-center gap-1.5">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to dashboard
        </Link>
        <Link to="/network" className="btn-ghost !py-2 !px-4 text-xs inline-flex items-center gap-1.5">
          Lifted Network <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );
}
